import { proxy, useSnapshot } from 'valtio';
import { addProductToCart, mergeCart } from '@/service/cart';
import {
  fetchCart,
  addProduct,
  removeProduct,
  updateProductQty,
  updateAllChecked,
  updateSelectedProduct,
  clearCart,
  clearCartInCache,
  checkHasProduct,
  editForm
} from '@/helper/cart';
import type { ICart, ICartProduct } from '@/service/cart/types';
import type { ICartProductGroup } from '@/views/cart/types';
import { getUserInfo } from '@/utils/token';

export enum EStorageMedia {
  /** 本地存储 */
  LOCAL,
  /** 远端存储 */
  REMOTE,
}

// 1. 缺失 购物车中全选、取消全选商品 接口
// 2. 缺失 购物车批量添加商品 接口

interface ICartStore {
  isLoading: boolean;
  isRemove: boolean;
  isclear: boolean;
  productMap: Record<string, ICartProduct>;
  list: ICartProduct[];
  cart?: ICart;
  groups: ICartProductGroup[];
  /** 存储类型 */
  storageMedia: EStorageMedia;

  /** 转换数据 */
  _convert: (cart: ICart) => void;

  /** 切换远程存储 */
  switchRemoteStorageMedia: () => Promise<void>;

  /** 更新商品数量 */
  updateQty: (id: string, qty: number) => Promise<void>;

  /** 更新商品选中状态 */
  updateChecked: (id: string, checked: boolean) => Promise<void>;

  /** 全选、取消全选商品 */
  updateAllChecked: (id: string[], checked: boolean) => Promise<void>;

  /** 删除商品 [支持批量删除] */
  remove: (id: string | string[]) => Promise<void>;
  changeRemoveState: () => Promise<void>;
  changeClearState: () => Promise<void>;
  /** 添加商品 */
  add: (product: Parameters<typeof addProductToCart>[number]) => Promise<void>;

  /** 清空购物车 */
  clear: () => Promise<void>;

  /** 刷新购物车数据 */
  refresh: (isGuest: boolean) => Promise<ICart>;

  /** 购物车中时候已存在该商品 */
  hasProduct: (id: string | string[]) => Promise<boolean>;

  edit: (skuCode: String, productName: String, productBrand: String, productSize: String, id: String) => Promise<void>;
}

export const toListByMap = (pool: ICartStore['productMap'] = {}) =>
  Reflect.ownKeys(pool).map(key => pool[key as string]);

export const toGroupsByList = (list: ICartStore['list'] = []) => {
  const groupsMap = list.reduce((sum, cur) => {
    const group = sum[cur.type!] || {
      name: cur.type!,
      code: cur.type!,
      list: [],
    };

    group.list.push(cur);

    return {
      ...sum,
      [cur.type!]: group,
    };
  }, {} as { [key: string]: ICartProductGroup });

  return Reflect.ownKeys(groupsMap)
    .map(item => groupsMap[item as string])
    .sort((a, b) => (a.name > b.name ? 1 : -1));
};

const computedList = (state: ICartStore, pool: ICartStore['productMap'] = {}) => {
  const list = toListByMap(pool);

  state.list = list;
  state.groups = toGroupsByList(list);
};

const state: ICartStore = proxy<ICartStore>({
  isLoading: false,
  isRemove: false,
  isclear: false,
  productMap: {},
  list: [],
  cart: undefined,
  groups: [],
  storageMedia: getUserInfo() ? EStorageMedia.REMOTE : EStorageMedia.LOCAL,
  _convert: cart => {
    const pool = (cart?.cartItemVOList || []).reduce(
      (sum, cur) => ({ ...sum, [cur.id]: cur }),
      {} as ICartStore['productMap']
    );
    computedList(state, pool);
  },

  switchRemoteStorageMedia: async () => {
    if (state.storageMedia === EStorageMedia.REMOTE) {
      return;
    }

    state.storageMedia = EStorageMedia.REMOTE;
    const cartCache = await fetchCart(EStorageMedia.LOCAL);
    const { cartItemVOList: cartItemsCache = [] } = cartCache;

    // 若本地无数据，直接返回
    if (cartItemsCache.length === 0) {
      return;
    }

    state.isLoading = true;
    const cart = await mergeCart(
      cartItemsCache.map(item => ({
        productName : item.productName,
        productSize: item.productSize,
        productBrand: item.productBrand,
        id: null,
        checked: item.checked,
        qty: item.qty,
        newItem: true,
        skuCode: item.skuCode,
      }))
    ).finally(() => (state.isLoading = false));

    state.cart = cart;
    state._convert(cart);
    // 清空本地数据
    clearCartInCache();
  },

  updateChecked: async (id, checked) => {
    const type = state.storageMedia;
    console.log(type, '健康大数据');
    // const pool = state.productMap || {};

    await updateSelectedProduct(type, { id, checked });

    // await fetchCart(type);
  },

  updateAllChecked: async (id, checked) => {
    // TODO: 待实现
    const type = state.storageMedia;
    await updateAllChecked(type, {id, checked});
    return Promise.resolve(checked as any);
  },

  updateQty: async (id, qty) => {
    const type = state.storageMedia;
    await updateProductQty(type, { id, qty });
    // await fetchCart(type);
  },

  remove: async id => {
    const type = state.storageMedia;
    // state.isRemove = true;
    await removeProduct(type, id).then(() => {
      state.isRemove = true;
    });
    // await fetchCart(type);
  },
  changeRemoveState: async () => {
    state.isRemove = false;
  },
  changeClearState: async () => {
    state.isclear = false;
  },
  add: async item => {
    console.log(item);
    const type = state.storageMedia;
    await addProduct(type, item);
    // await fetchCart(type);
  },

  clear: async () => {
    state.cart = undefined;
    state.productMap = {};
    state.list = [];
    state.groups = [];
    state.isLoading = false;
    const type = state.storageMedia;
    await clearCart(type).then(() => {
      state.isclear = true;
    });;
    // await fetchCart(type);
  },

  refresh: async isGuest => {
    state.isLoading = true;
    const type = isGuest ? EStorageMedia.LOCAL : EStorageMedia.REMOTE;
    const cart = await fetchCart(type).finally(() => (state.isLoading = false));
    state.cart = cart;
    state._convert(cart);
    return cart;
  },

  hasProduct: async skuCode => {
    const type = state.storageMedia;
    return checkHasProduct(type, skuCode);
  },

  edit: async (skuCode, productName, productBrand, productSize, id) => {
    const type = state.storageMedia;
    await editForm(type, skuCode, productName, productBrand, productSize, id);
    await fetchCart(type);
  }
});

export const cartStore = state;

/** 获取购物车状态 */
export const useSnapshotCart = () => useSnapshot(cartStore) as ICartStore;
